'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
export type PhysicianRole = 'solo_i' | 'solo_ii' | 'solo_iii' | 'admin' | 'clinical_staff';
export type Permission = string;
export type ScreenKey = string;

export const ROLE_LABELS: Record<PhysicianRole, string> = {
  solo_i: 'SOLO I',
  solo_ii: 'SOLO II',
  solo_iii: 'SOLO III',
  admin: 'Administrator',
  clinical_staff: 'Personel kliniczny',
};

export const ROLE_COLORS: Record<PhysicianRole, string> = {
  solo_i: 'bg-blue-50 text-blue-700 border-blue-200',
  solo_ii: 'bg-violet-50 text-violet-700 border-violet-200',
  solo_iii: 'bg-amber-50 text-amber-700 border-amber-200',
  admin: 'bg-red-50 text-red-700 border-red-200',
  clinical_staff: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const SCREEN_PERMISSIONS: Record<string, string> = {};
export const ROLE_PERMISSIONS: Record<PhysicianRole, Permission[]> = {
  admin: [], solo_i: [], solo_ii: [], solo_iii: [], clinical_staff: [],
};

// ─── Hardcoded admin emails ───────────────────────────────────────────────────
const HARDCODED_ADMIN_EMAILS = [
  'admin@nio.pl',
  'rozkwitaj@solo3.gliwice',
  'admin@solo3.gliwice',
  'prof.rozkwitaj@nio.pl',
];

const VALID_ROLES: PhysicianRole[] = ['solo_i', 'solo_ii', 'solo_iii', 'admin', 'clinical_staff'];

function isValidRole(value: unknown): value is PhysicianRole {
  return typeof value === 'string' && VALID_ROLES.includes(value as PhysicianRole);
}

// ─── Context ──────────────────────────────────────────────────────────────────
interface RoleContextValue {
  role: PhysicianRole | null;
  permissions: Permission[];
  loading: boolean;
  isAdmin: boolean;
  can: (permission: Permission) => boolean;
  canAccess: (screen: ScreenKey) => boolean;
  setRole: (role: PhysicianRole) => void;
}

const RoleContext = createContext<RoleContextValue>({
  role: null,
  permissions: [],
  loading: true,
  isAdmin: false,
  can: () => true,
  canAccess: () => true,
  setRole: () => {},
});

export const useRole = () => useContext(RoleContext);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRoleState] = useState<PhysicianRole | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    let cancelled = false;

    const resolveRole = async () => {
      try {
        // Use getUser() for authoritative server-side check
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error || !user || cancelled) {
          if (!cancelled) setLoading(false);
          return;
        }

        const email = user.email?.toLowerCase() ?? '';

        // ── Phase 1: Hardcoded admin email check ──────────────────────────────
        if (HARDCODED_ADMIN_EMAILS.some(e => e.toLowerCase() === email)) {
          if (!cancelled) { setRoleState('admin'); setLoading(false); }
          return;
        }

        // ── Phase 2: Auth metadata check ──────────────────────────────────────
        const metaRole =
          user.app_metadata?.system_role ||
          user.app_metadata?.role ||
          user.user_metadata?.system_role ||
          user.user_metadata?.role;

        if (isValidRole(metaRole)) {
          if (!cancelled) { setRoleState(metaRole); setLoading(false); }
          return;
        }

        // ── Phase 3: Fallback — query managed_users by email ──────────────────
        const { data: managedUser, error: dbError } = await supabase
          .from('managed_users')
          .select('system_role')
          .ilike('email', email)
          .maybeSingle();

        if (!dbError && managedUser && isValidRole(managedUser.system_role)) {
          if (!cancelled) { setRoleState(managedUser.system_role); setLoading(false); }
          return;
        }

        // No role found — default to solo_i
        if (!cancelled) { setRoleState('solo_i'); setLoading(false); }
      } catch {
        if (!cancelled) { setRoleState('solo_i'); setLoading(false); }
      }
    };

    resolveRole();

    // Re-resolve on auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        if (!cancelled) { setRoleState(null); setLoading(false); }
      } else {
        resolveRole();
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const setRole = (newRole: PhysicianRole) => {
    // Only allow upgrades to admin, never downgrades from admin
    if (role === 'admin') return;
    setRoleState(newRole);
  };

  const isAdmin = role === 'admin';

  const value: RoleContextValue = {
    role,
    permissions: [],
    loading,
    isAdmin,
    can: () => true,
    canAccess: () => true,
    setRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
